import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Stack, Typography } from '@mui/material_modern'
import { v4 as uuidv4 } from 'uuid'
import { cloneDeep } from 'lodash'

// services
import { useGetCustomerQuery } from 'services/customers'
import { useGetDriversQuery } from 'services/drivers'
import { useGetAssetsByCategoryQuery } from 'services/physicalAssets'
import {
  useGetDispatchTicketQuery,
  useVoidDispatchTicketMutation,
  useArchiveRecurringDispatchTicketMutation,
} from 'services/dispatch'

// state
import { setInitialAssets } from 'state/dispatch/index'

// components
import AssetList from 'components/containers/load-board/components/AssetList'
import SubFormLayout from 'components/layouts/SubFormLayout'
import RouterPrompt from 'components/generic/modals/router-prompt/RouterPrompt'
import PrintDialog from 'components/generic/printable/PrintDialog'
import PrintableDispatchTicket from 'components/generic/printable/PrintableDispatchTicket'
import TransportInfoFormRow from 'components/containers/load-board/components/TransportInfoFormRow'
import CompletionInfoFormRow from 'components/containers/load-board/components/CompletionInfoFormRow'
import { useDispatchHeader } from 'components/containers/load-board/utils/useDispatchHeader'
import { useDispatchTicket } from 'components/containers/load-board/utils/useDispatchTicket'
import { useHandleTypeChange } from '../utils/useHandleTypeChange'
import { formatGetDispatchTicket } from 'components/containers/load-board/utils/formatFunctions'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import GeneralInfoForm from 'components/containers/load-board/components/GeneralInfoForm'
import CustomerInfoForm from 'components/containers/load-board/components/CustomerInfoForm'
import PrintableMiniDispatch from 'components/generic/printable/PrintableMiniDispatch'

// utils
import { trackEvent } from 'utils/tracking'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import {
  ASSET_CATEGORY,
  CHURN_ZERO_EVENTS,
  DISPATCH_TICKET_STATUSES,
  DISPATCH_TICKET_TYPES,
  DT_STATUS_ENUM,
  GS_ERROR_CODE,
  RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS,
} from 'utils/constants'
import { formOptions } from 'utils/validation/dispatchTicket'
import { dispatchSnackbar, locationIsNotEmpty, contactIsNotEmpty } from 'utils/helpers'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { MutationActionCreatorResult } from '@reduxjs/toolkit/dist/query/core/buildInitiate'
import { capAllWords } from 'utils/data-manipulation/common'

// This is used when navigating to this component from a duplicate DT button
interface LocationState {
  duplicateData?: {
    id: string
  }
}

const DispatchTicketContainer = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const locationState = routerLocation.state as LocationState | undefined

  const { id: dispatchTicketId } = useParams()
  const isUpdate = !!dispatchTicketId

  // form
  const methods = useForm(formOptions)
  const {
    reset,
    watch,
    setValue,
    getValues,
    handleSubmit,
    trigger,
    formState: { dirtyFields },
  } = methods
  const isDirty = Object.keys(dirtyFields).length > 0

  const { inOrgMode } = useAppSelector(state => state.ui as any)
  const { settings } = useAppSelector(state => state.settings)
  const { yardDetailsList, yardId } = useAppSelector(state => state.auth)
  const { pickupAssets, dropoffAssets, pickAndReturnAssets, deliveryAssets, liveLoadAssets } = useAppSelector(
    state => state.dispatch,
  )
  const { voidRecurringDt, voidTicketAndRecurrence, enableMultiLocationYards } = useAppSelector(
    state => state.featureFlags?.variables || {},
  )
  const ticketStatus = watch('status')
  const ticketIsResolved = useMemo(() => {
    return (
      [
        DISPATCH_TICKET_STATUSES.COMPLETED.id,
        DISPATCH_TICKET_STATUSES.FAILED.id,
        DISPATCH_TICKET_STATUSES.VOID.id,
      ].indexOf(ticketStatus) > -1
    )
  }, [ticketStatus])

  const selectedDispatchTicketType = watch('type')
  const selectedLocationId = watch('location.id')
  const selectedTrailerId = watch('assetTrailerId')
  /* dispatchingYardLocationId represents the location of the yard where the truck is leaving from
  typically a truck will return to the same yard it left from but not always if the ticket is between yards in an organization */
  const dispatchingYardLocationId = watch('dispatchingYardLocationId')

  const ticketHasAssets = !!(
    (pickupAssets || []).length > 0 ||
    (dropoffAssets || []).length > 0 ||
    (pickAndReturnAssets || []).length > 0 ||
    (deliveryAssets || []).length > 0 ||
    (liveLoadAssets || []).length > 0 ||
    selectedTrailerId
  )

  const [hasEditedAssets, setHasEditedAssets] = useState(false)
  const [assetShouldDirty, setAssetShouldDirty] = useState(false)
  const [openPrintDialog, setOpenPrintDialog] = useState(false)
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [showRevertModal, setShowRevertModal] = useState(false)
  const [showTypeChangeModal, setShowTypeChangeModal] = useState(false)
  const idempotencyKey = useRef(uuidv4())
  // Used to store the new type during the confirmation modal:
  const [attemptedNewType, setAttemptedNewType] = useState(null)
  const [contactOptions, setContactOptions] = useState([] as any)

  const [showDeleteRecurringTicketModal, setShowDeleteRecurringTicketModal] = useState(false)

  const [voidDispatchTicket, { isLoading: isVoidingDispatchTicket }] = useVoidDispatchTicketMutation()
  const [archiveRecurringDispatchTicket, { isLoading: isArchivingRecurringDispatchTicket }] =
    useArchiveRecurringDispatchTicketMutation()

  const contact = watch('contactId')
  const locationWatch = watch('location')

  const setLocation = (newLocation, shouldDirty = true) => {
    if (newLocation) {
      setValue(
        'location',
        {
          name: newLocation.name || '',
          city: newLocation.city || '',
          state: newLocation.state || '',
          zip: newLocation.zip || '',
          id: newLocation.id || newLocation.name || newLocation.locationName || '',
          address: newLocation.shippingAddress || newLocation.address1 || newLocation.address || '',
          notes: newLocation.notes || '',
        },
        { shouldDirty },
      )

      if (newLocation.id) trigger('location.id')
    }
  }

  // RTK Query Data
  const [customerId, setCustomerId] = useState('')

  const [hasFetchedDuplicate, setHasFetchedDuplicate] = useState(false)

  // Fetch duplicate ticket data only once
  const { data: duplicateTicketData, isLoading: isLoadingDuplicateTicketData } = useGetDispatchTicketQuery(
    { dispatchTicketId: locationState?.duplicateData?.id || '' },
    { skip: !locationState?.duplicateData || hasFetchedDuplicate },
  )

  // Updates the starting form when we are duplicating a ticket
  useEffect(() => {
    if (duplicateTicketData) {
      setValue('type', duplicateTicketData.type, { shouldDirty: true })
      setValue('driverId', duplicateTicketData.driverId, { shouldDirty: true })

      duplicateTicketData.dispatchTicketAssets?.forEach(ticketAsset => {
        if (ticketAsset.asset?.assetCategory === ASSET_CATEGORY.TRUCK) {
          setValue('assetTruckId', ticketAsset.assetId, { shouldDirty: true })
        }
        if (ticketAsset.asset?.assetCategory === ASSET_CATEGORY.TRAILER) {
          setValue('assetTrailerId', ticketAsset.assetId, { shouldDirty: true })
        }
      })

      // Org Mode data
      if (inOrgMode) {
        setValue('dispatchingYardLocationId', duplicateTicketData.interYard?.startLocationId)
        setValue('terminatingYardLocationId', duplicateTicketData.interYard?.endLocationId)
      }

      setCustomerId(duplicateTicketData.customerId)

      setHasFetchedDuplicate(true)
    }
  }, [duplicateTicketData])

  /**
   * Render asset lists based on dispatch ticket type
   * its possible we'll need to render both pick up and drop off asset lists side by side in the case of an exchange
   * @return {*[]}
   */
  const renderAssetsList = () => {
    const commonProps = {
      methods,
      disabled: !customerId || !selectedLocationId || ticketIsResolved || ticketStatus === DT_STATUS_ENUM.REQUESTED,
      ticketIsResolved,
      setHasEditedAssets,
      assetShouldDirty,
      setAssetShouldDirty,
      ticketStatus,
    }
    const assetLists: ReactElement[] = []

    if ([DISPATCH_TICKET_TYPES.PICK_UP.id, DISPATCH_TICKET_TYPES.EXCHANGE.id].includes(selectedDispatchTicketType)) {
      assetLists.push(
        <AssetList
          key={uuidv4()}
          {...commonProps}
          type={DISPATCH_TICKET_TYPES.PICK_UP.id}
          fromLocationId={selectedLocationId}
        />,
      )
    }
    if ([DISPATCH_TICKET_TYPES.DROP_OFF.id, DISPATCH_TICKET_TYPES.EXCHANGE.id].includes(selectedDispatchTicketType)) {
      assetLists.push(
        <AssetList
          key={uuidv4()}
          {...commonProps}
          type={DISPATCH_TICKET_TYPES.DROP_OFF.id}
          fromLocationId={dispatchingYardLocationId}
        />,
      )
    }
    if ([DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id].includes(selectedDispatchTicketType)) {
      assetLists.push(
        <AssetList
          key={uuidv4()}
          {...commonProps}
          type={DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id}
          fromLocationId={selectedLocationId}
        />,
      )
    }
    if ([DISPATCH_TICKET_TYPES.DELIVERY.id].includes(selectedDispatchTicketType)) {
      assetLists.push(
        <AssetList
          key={uuidv4()}
          {...commonProps}
          type={DISPATCH_TICKET_TYPES.DELIVERY.id}
          fromLocationId={dispatchingYardLocationId}
        />,
      )
    }
    if ([DISPATCH_TICKET_TYPES.LIVE_LOAD.id].includes(selectedDispatchTicketType)) {
      assetLists.push(
        <AssetList
          key={uuidv4()}
          {...commonProps}
          type={DISPATCH_TICKET_TYPES.LIVE_LOAD.id}
          fromLocationId={dispatchingYardLocationId}
        />,
      )
    }
    return assetLists
  }

  const { data: customerData, isLoading: isLoadingCustomerData } = useGetCustomerQuery(
    { customerId },
    { refetchOnMountOrArgChange: true, skip: !customerId },
  )

  const { data: driversData, isFetching: isFetchingDriversData } = useGetDriversQuery({
    filter: JSON.stringify({ isActive: true }),
  })
  const { data: trucksData, isLoading: isLoadingTruckData } = useGetAssetsByCategoryQuery({
    assetCategory: ASSET_CATEGORY.TRUCK,
    filter: JSON.stringify({ locationId: dispatchingYardLocationId }),
  })

  const { onSubmitDispatchTicket, onSubmitRecurringDispatchTicket, dispatchTicketData, isLoadingDispatchTicket } =
    useDispatchTicket({
      dispatchTicketId,
      methods,
    })

  const { onTypeChange, handleTaskTypeChangeConfirm } = useHandleTypeChange({
    ticketHasAssets,
    setAttemptedNewType,
    setShowTypeChangeModal,
    methods,
  })

  const isLoading =
    (dispatchTicketData && !customerData) ||
    isLoadingCustomerData ||
    isLoadingDispatchTicket ||
    isLoadingTruckData ||
    isFetchingDriversData ||
    isVoidingDispatchTicket ||
    isLoadingDuplicateTicketData ||
    isArchivingRecurringDispatchTicket

  useGlobalLoader(isLoading)

  // If we are attempting to view a ticket for a yard we are not logged in to, we navigate back to the load board
  useEffect(() => {
    if (!dispatchTicketData || !yardId) return

    if (!inOrgMode && dispatchTicketData.yardId !== yardId) {
      const yardText = dispatchTicketData.yard?.name
        ? `${capAllWords(dispatchTicketData.yard.name)}`
        : 'the correct yard' // fallback if the yard object is missing

      dispatchSnackbar(
        dispatch,
        'info',
        `Unable to view Dispatch Ticket. Switch to ${yardText} to view ticket #${dispatchTicketData.externalId}`,
      )
      navigate('/app/load-board/active-tickets')
    }
  }, [dispatchTicketData, yardId, inOrgMode])

  useEffect(() => {
    if (!yardDetailsList) return

    if (!inOrgMode) {
      const dispatchingYard = yardDetailsList.find(({ id }) => id === yardId)
      const locationIdOfDispatchingYard =
        dispatchingYard?.defaultLocationId && enableMultiLocationYards
          ? dispatchingYard?.defaultLocationId
          : dispatchingYard?.locationId

      setValue('dispatchingYardLocationId', locationIdOfDispatchingYard)
    } else if (inOrgMode && dispatchTicketData) {
      if (dispatchTicketData.interYard) {
        setValue('dispatchingYardLocationId', dispatchTicketData.interYard.startLocationId)
        setValue('terminatingYardLocationId', dispatchTicketData.interYard.endLocationId)
      } else {
        setValue('dispatchingYardLocationId', yardId)
      }
    }
  }, [dispatchTicketData, yardId, yardDetailsList, inOrgMode, setValue])

  //? populate form & redux state with dispatch ticket data or empty arrays
  useEffect(() => {
    if (dispatchTicketData && yardDetailsList) {
      // we can't use the form data prop 'dispatchingYardLocationId'/'terminatingYardLocationId' bc it hasn't been set yet
      const dispatchTicketYardId = dispatchTicketData?.yardId
      const dispatchingYardFromDtData = yardDetailsList.find(({ id }) => id === dispatchTicketYardId)
      const dispatchingYardLocationIdFromDtData =
        dispatchingYardFromDtData?.defaultLocationId && enableMultiLocationYards
          ? dispatchingYardFromDtData?.defaultLocationId
          : dispatchingYardFromDtData?.locationId

      const startYardLocationId = dispatchTicketData.interYard
        ? dispatchTicketData.interYard.startLocationId
        : dispatchingYardLocationIdFromDtData

      const endYardLocationId = dispatchTicketData.interYard
        ? dispatchTicketData.interYard.endLocationId
        : dispatchingYardLocationIdFromDtData

      const { locationId: customerLocationId, dispatchTicketAssets } = dispatchTicketData
      setCustomerId(dispatchTicketData?.customerId)

      const ticketStatusIsFailedOrVoid =
        dispatchTicketData?.status === DISPATCH_TICKET_STATUSES.FAILED.id ||
        dispatchTicketData?.status === DISPATCH_TICKET_STATUSES.VOID.id

      const ticketPickupAssets =
        dispatchTicketAssets?.filter(
          dispatchTicketAsset =>
            (ticketStatusIsFailedOrVoid ? customerLocationId : endYardLocationId) ===
              dispatchTicketAsset.toLocationId && dispatchTicketAsset.asset.assetCategory === ASSET_CATEGORY.CONTAINER,
        ) || []
      const ticketDropoffAssets =
        dispatchTicketAssets?.filter(
          dispatchTicketAsset =>
            (ticketStatusIsFailedOrVoid ? startYardLocationId : customerLocationId) ===
              dispatchTicketAsset.toLocationId && dispatchTicketAsset.asset.assetCategory === ASSET_CATEGORY.CONTAINER,
        ) || []
      const ticketPickAndReturnAssets =
        dispatchTicketAssets?.filter(
          dispatchTicketAsset =>
            customerLocationId === dispatchTicketAsset.toLocationId &&
            dispatchTicketAsset.asset.assetCategory === ASSET_CATEGORY.CONTAINER,
        ) || []

      const ticketDeliveryAssets =
        dispatchTicketAssets?.filter(dispatchTicketAsset => {
          return (
            endYardLocationId === dispatchTicketAsset.toLocationId &&
            dispatchTicketAsset.asset.assetCategory === ASSET_CATEGORY.CONTAINER
          )
        }) || []
      // from an asset POV delivery and live load are the same. In either case container leaves from and returns to dispatching yard
      const ticketLiveLoadAssets = cloneDeep(ticketDeliveryAssets)

      const isPickup = dispatchTicketData?.type === DISPATCH_TICKET_TYPES.PICK_UP.id
      const isDropoff = dispatchTicketData?.type === DISPATCH_TICKET_TYPES.DROP_OFF.id
      const isExchange = dispatchTicketData?.type === DISPATCH_TICKET_TYPES.EXCHANGE.id
      const isPickAndReturn = dispatchTicketData?.type === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id
      const isDelivery = dispatchTicketData?.type === DISPATCH_TICKET_TYPES.DELIVERY.id
      const isLiveLoad = dispatchTicketData?.type === DISPATCH_TICKET_TYPES.LIVE_LOAD.id

      if (isPickAndReturn) {
        dispatch(
          setInitialAssets({ ticketType: DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id, assets: ticketPickAndReturnAssets }),
        )
      }
      if (isDelivery) {
        dispatch(setInitialAssets({ ticketType: DISPATCH_TICKET_TYPES.DELIVERY.id, assets: ticketDeliveryAssets }))
      }
      if (isLiveLoad) {
        dispatch(setInitialAssets({ ticketType: DISPATCH_TICKET_TYPES.LIVE_LOAD.id, assets: ticketLiveLoadAssets }))
      }
      // for an exchange type ticket we'll need to set both pickup and dropoff assets
      if ((isPickup || isExchange) && ticketPickupAssets.length > 0) {
        dispatch(setInitialAssets({ ticketType: DISPATCH_TICKET_TYPES.PICK_UP.id, assets: ticketPickupAssets }))
      }
      if ((isDropoff || isExchange) && ticketDropoffAssets.length > 0) {
        dispatch(setInitialAssets({ ticketType: DISPATCH_TICKET_TYPES.DROP_OFF.id, assets: ticketDropoffAssets }))
      }
      setValue('type', dispatchTicketData?.type)
      reset(formatGetDispatchTicket(dispatchTicketData))
    }
    return () => {
      dispatch(setInitialAssets({ ticketType: DISPATCH_TICKET_TYPES.DROP_OFF.id, assets: [] }))
      dispatch(setInitialAssets({ ticketType: DISPATCH_TICKET_TYPES.PICK_UP.id, assets: [] }))
      dispatch(setInitialAssets({ ticketType: DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id, assets: [] }))
      dispatch(setInitialAssets({ ticketType: DISPATCH_TICKET_TYPES.DELIVERY.id, assets: [] }))
      dispatch(setInitialAssets({ ticketType: DISPATCH_TICKET_TYPES.LIVE_LOAD.id, assets: [] }))
    }
  }, [dispatchTicketData])

  const eventName = CHURN_ZERO_EVENTS[`${isUpdate ? 'PATCH' : 'CREATE'}_DISPATCH_TICKET`]
  const handleSuccess = (data, response) => {
    reset(data)
    setHasEditedAssets(false)
    setAssetShouldDirty(false)

    trackEvent(eventName)
    dispatchSnackbar(dispatch, 'success', `Dispatch ticket successfully ${isUpdate ? 'updated' : 'created'}`)
    if (isUpdate) return

    const dispatchTicketId = response?.dispatchTicket ? response.dispatchTicket.id : response?.id
    navigate(`/app/load-board/ticket-history/${dispatchTicketId || ''}`)
  }

  const handleError = error => {
    let errorMessage = error?.data?.code || `There was an error ${isUpdate ? 'updating' : 'creating'} the ticket`

    if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
      errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
    }

    if (error?.data?.error === 'Unable to revert. Asset has been moved since finishing this ticket.') {
      errorMessage = `Unable to revert: Since finishing the ticket, one or more assets have changed locations.`
    }

    if (error?.data?.error === 'Unable to revert. Asset is currently attached to a different dispatch ticket.') {
      errorMessage = `Unable to revert: One of more assets are currently on a different Dispatch Ticket.`
    }

    trackEvent({ eventName, errorMessage })

    dispatchSnackbar(dispatch, 'error', errorMessage)
  }

  const onSubmit = async (data, config = {}) => {
    const recurringRule = getValues('recurringRule')
    if (isUpdate || !recurringRule || recurringRule?.frequency === RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS.NONE.id) {
      await onSubmitDispatchTicket(data, {
        handleSuccess,
        handleError,
        idempotencyKey: idempotencyKey.current,
        ...config,
      })
    } else {
      await onSubmitRecurringDispatchTicket(data, recurringRule, yardId, {
        handleSuccess,
        handleError,
        idempotencyKey: idempotencyKey.current,
        ...config,
      })
    }
  }

  const onDeleteRecurringDispatchTicket = async () => {
    if (!dispatchTicketData?.recurringDispatchTicketId) {
      return
    }

    await archiveRecurringDispatchTicket({
      recurringDispatchTicketId: dispatchTicketData.recurringDispatchTicketId,
      dispatchTicketId: voidRecurringDt ? dispatchTicketData.id : undefined,
    })
      .unwrap()
      .then(() => {
        navigate('/app/load-board/active-tickets')
        dispatchSnackbar(dispatch, 'success', 'Recurring dispatch tickets deleted')
      })
      .catch(error => {
        let errorMessage = `An error occurred deleting the recurring dispatch tickets.`

        if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
          errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
        }

        dispatchSnackbar(dispatch, 'error', errorMessage)
      })
  }

  const deleteDispatchTicket = async (ticketAndRecurring?: boolean) => {
    setShowVoidModal(false)
    if (!dispatchTicketData || !dispatchTicketData.id) {
      return
    }
    if (dispatchTicketData.status === DISPATCH_TICKET_STATUSES.VOID.id) {
      dispatchSnackbar(dispatch, 'error', 'This dispatch ticket is already marked as VOID')
      return
    }

    let deletePromise: MutationActionCreatorResult<any> | undefined
    if (ticketAndRecurring) {
      if (dispatchTicketData.recurringDispatchTicketId) {
        deletePromise = archiveRecurringDispatchTicket({
          recurringDispatchTicketId: dispatchTicketData.recurringDispatchTicketId,
          dispatchTicketId: dispatchTicketData.id,
          deleteDispatchTicket: true,
        })
      }
    } else {
      deletePromise = voidDispatchTicket({
        voidDispatchTicketBody: dispatchTicketData,
        dispatchTicketId: dispatchTicketData.id,
      })
    }
    if (deletePromise) {
      await deletePromise
        .unwrap()
        .then(() => {
          navigate('/app/load-board/active-tickets')
          dispatchSnackbar(dispatch, 'success', 'Dispatch Ticket Voided')
        })
        .catch(error => {
          let errorMessage = `An error occurred voiding the dispatch ticket.`

          if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
            errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
          }

          dispatchSnackbar(dispatch, 'error', errorMessage)
        })
    }
    return
  }

  const onClickVoidDispatchTicket = async () => {
    deleteDispatchTicket()
  }

  const onClickVoidThisAndFutureDispatchTickets = async () => {
    deleteDispatchTicket(true)
  }

  const onDuplicateDispatchTicket = () => {
    navigate('/app/load-board/ticket-history/new', { state: { duplicateData: dispatchTicketData } })
  }

  const onRevertDispatchTicket = () => {
    const currentValues = getValues()
    let newTicket

    if (currentValues.status === DT_STATUS_ENUM.IN_PROGRESS) {
      newTicket = dispatchTicketData?.driverId
        ? {
            ...dispatchTicketData,
            ...currentValues,
            status: DT_STATUS_ENUM.ASSIGNED,
          }
        : {
            ...dispatchTicketData,
            ...currentValues,
            status: DT_STATUS_ENUM.UNASSIGNED,
          }
    } else {
      newTicket = {
        ...dispatchTicketData,
        ...currentValues,
        status: DT_STATUS_ENUM.IN_PROGRESS,
      }
    }

    onSubmit(newTicket)
    setShowRevertModal(false)
  }

  const [openMiniPrintDialog, setOpenMiniPrintDialog] = useState(false)

  useDispatchHeader({
    dispatchTicketData,
    hasEditedAssets,
    methods,
    onSubmit,
    setOpenPrintDialog,
    setOpenMiniPrintDialog,
    setShowVoidModal,
    onDuplicateDispatchTicket,
    setShowRevertModal,
  })

  const [locationOptions, setLocationOptions] = useState([] as any)
  const setIntercompanyValue = (intercompany: boolean) => {
    setValue('intercompany', intercompany, { shouldDirty: false })
  }
  const setCustomerValue = (customer?, dispatchTicket?, shouldDirty = true) => {
    setCustomerId(customer?.id || '')
    setValue('customer.id', customer?.id || '', { shouldDirty })
    setValue('customer.displayName', customer?.displayName || '', { shouldDirty })
    setValue('customer.externalId', customer?.externalId || '', { shouldDirty })
    setValue('customer.supplierType', customer?.supplierType || '', { shouldDirty })
    setValue('customer.data', customer || null, { shouldDirty })

    const validCustomerContacts =
      customer?.contacts
        ?.filter(contact => contactIsNotEmpty(contact))
        ?.sort((a, b) => (b.isPrimaryContact || false) - (a.isPrimaryContact || false))
        ?.map(contact => ({
          id: contact.id,
          name: contact.firstName + ' ' + contact.lastName,
        })) || []
    setContactOptions(validCustomerContacts)

    const validCustomerLocations = customer?.locations?.filter(location => locationIsNotEmpty(location)) || []
    setLocationOptions(validCustomerLocations)

    const ticketSource = locationState?.duplicateData?.id ? duplicateTicketData : dispatchTicket

    if (shouldDirty) {
      const currentContactId = ticketSource?.contactId || validCustomerContacts?.[0]?.id || null
      setValue('contactId', currentContactId, { shouldDirty: false })
    }

    setLocation(
      validCustomerLocations?.find(location => location.id === ticketSource?.locationId) ||
        validCustomerLocations?.[0] ||
        {},
      shouldDirty,
    )
  }

  useEffect(() => {
    if (customerData === undefined) return
    const data = getValues()
    setCustomerValue(customerData, data, false)
  }, [customerData, dispatchTicketData])

  const showSecondaryButton = voidTicketAndRecurrence && dispatchTicketData?.recurringDispatchTicketId

  return (
    <FormProvider {...methods} onSubmit={handleSubmit(onSubmit)}>
      <RouterPrompt
        title="Leave This Page"
        question="Are you sure you want to leave this page?"
        description="You have unsaved data on this page and all unsaved data on the ticket will be lost."
        confirmLabel="YES, LEAVE PAGE"
        cancelLabel="GO BACK"
        when={isDirty || hasEditedAssets}
      />
      {(dispatchTicketData?.status === DT_STATUS_ENUM.COMPLETED ||
        dispatchTicketData?.status === DT_STATUS_ENUM.FAILED) && (
        <Stack p={3}>
          <CompletionInfoFormRow dispatchTicketData={dispatchTicketData} />
        </Stack>
      )}
      <Stack
        direction={{
          xs: 'column',
          lg: 'row',
        }}
        justifyContent="space-between"
        p={3}
        gap={2}
        sx={{
          '& > *': {
            width: {
              xs: '100% !important',
              lg: '50% !important',
            },
          },
        }}
      >
        <GeneralInfoForm
          dispatchTicketData={dispatchTicketData}
          ticketIsResolved={ticketIsResolved}
          yardDetailsList={yardDetailsList}
          onTypeChange={onTypeChange}
          onDeleteRecurringTickets={() => setShowDeleteRecurringTicketModal(true)}
          isLoading={isLoading}
          setIntercompanyValue={setIntercompanyValue}
          ticketHasAssets={ticketHasAssets}
        />
        <CustomerInfoForm
          customerId={customerId}
          ticketHasAssets={ticketHasAssets}
          setValue={setValue}
          setCustomerValue={setCustomerValue}
          locationOptions={locationOptions}
          contactOptions={contactOptions}
          setLocation={setLocation}
          supplierTypeIds={settings.dispatchSettings?.loadBoard?.supplierTypesFilter}
        />
      </Stack>
      <Stack p={3}>
        <Typography variant="h6" gutterBottom color="textSecondary">
          Assets Information
        </Typography>
        <Stack direction="row" spacing={1}>
          {/* render asset list with correct props based on DT type */}
          {renderAssetsList()}
        </Stack>
      </Stack>
      <Stack p={3}>
        <TransportInfoFormRow
          customerId={customerId}
          ticketIsResolved={ticketIsResolved}
          dispatchTicketData={dispatchTicketData}
          driversData={driversData}
          trucksData={trucksData}
        />
      </Stack>
      <Stack p={3}>
        <SubFormLayout
          formName="Notes"
          boxStyles={{ width: '100%' }}
          fields={[
            [
              {
                name: 'note',
                label: 'Ticket Specific Notes',
                dataTest: 'note',
                defaultValue: '',
                fieldType: 'input',
                InputProps: {
                  multiline: true,
                  rows: 3,
                },
              },
              {
                name: 'location.notes',
                label: 'Recurring Site Notes',
                dataTest: 'location-notes',
                defaultValue: '',
                fieldType: 'static',
                InputProps: {
                  multiline: true,
                  rows: 3,
                },
              },
              ...(dispatchTicketData?.requestNotes
                ? [
                    {
                      name: 'requestNotes',
                      label: 'Portal Request Notes',
                      dataTest: 'request-notes',
                      defaultValue: '',
                      fieldType: 'static',
                      InputProps: {
                        multiline: true,
                        rows: 3,
                      },
                    },
                  ]
                : []),
            ],
          ]}
        />
      </Stack>
      {openPrintDialog && (
        <PrintDialog
          title={`Dispatch Ticket No. ${dispatchTicketData?.externalId}`}
          open={openPrintDialog}
          onClose={() => setOpenPrintDialog(false)}
          showLogoAddress
        >
          <PrintableDispatchTicket
            customerData={customerData}
            locationData={getValues('location')}
            dispatchTicketData={dispatchTicketData}
            pickupAssets={pickupAssets}
            dropoffAssets={dropoffAssets}
            pickAndReturnAssets={pickAndReturnAssets}
            deliveryAssets={deliveryAssets}
            liveLoadAssets={liveLoadAssets}
          />
        </PrintDialog>
      )}
      {openMiniPrintDialog && (
        <PrintDialog
          title={`Mini Tags - Dispatch Ticket No. ${dispatchTicketData?.externalId}`}
          open={openMiniPrintDialog}
          onClose={() => setOpenMiniPrintDialog(false)}
          showLogoAddress={false}
        >
          <PrintableMiniDispatch
            customerName={customerData?.displayName || ''}
            dispatchNumber={Number(dispatchTicketData?.externalId)}
            scheduleDate={dispatchTicketData?.scheduleDate || ''}
          />
        </PrintDialog>
      )}
      {showVoidModal && (
        <AlertModal
          open={showVoidModal}
          modalType="void"
          title="Void This Dispatch Ticket"
          question="Are you sure you want to void this dispatch ticket?"
          description="All assets attached to this dispatch ticket will be returned to their previous status"
          confirmLabel="YES, VOID THIS DISPATCH TICKET"
          cancelLabel="GO BACK"
          onClose={() => {
            setShowVoidModal(false)
          }}
          data-testid="void-dispatch-ticket-modal"
          onSubmit={onClickVoidDispatchTicket}
          secondaryActionLabel={showSecondaryButton ? 'VOID THIS AND ALL RECURRING DISPATCH TICKETS' : undefined}
          onSecondaryAction={showSecondaryButton ? onClickVoidThisAndFutureDispatchTickets : undefined}
          buttonBoxWidth={showSecondaryButton ? '84%' : undefined}
          height={showSecondaryButton ? 416 : undefined}
        />
      )}
      {showRevertModal && (
        <AlertModal
          open={showRevertModal}
          modalType="column"
          title="Revert Ticket Status"
          question={`Are you sure you want to revert the status of this ticket to "${dispatchTicketData?.status === DT_STATUS_ENUM.IN_PROGRESS ? (dispatchTicketData?.driverId ? 'Assigned' : 'Unassigned') : 'In Progress'}"?`}
          confirmLabel="YES, REVERT STATUS"
          description=""
          cancelLabel="CANCEL"
          onClose={() => {
            setShowRevertModal(false)
          }}
          data-testid="revert-dispatch-ticket-modal"
          onSubmit={onRevertDispatchTicket}
        />
      )}
      {showTypeChangeModal && (
        <AlertModal
          open={showTypeChangeModal}
          modalType="ship"
          title="Change Task Type"
          question="Are you sure you'd like to change the task type?"
          description="Any containers on this ticket will be removed."
          confirmLabel="YES"
          cancelLabel="GO BACK"
          onClose={() => {
            setShowTypeChangeModal(false)
          }}
          data-testid="change-type-dispatch-ticket-modal"
          onSubmit={() => handleTaskTypeChangeConfirm(attemptedNewType)}
        />
      )}
      {showDeleteRecurringTicketModal && (
        <AlertModal
          open={showDeleteRecurringTicketModal}
          modalType="void"
          title="Delete Recurring Dispatch Tickets"
          question="Are you sure you want to stop this dispatch ticket from recurring in the future?"
          description="This will delete all unedited, future copies of this dispatch ticket."
          confirmLabel="YES, END RECURRENCE"
          cancelLabel="CANCEL"
          onClose={() => {
            setShowDeleteRecurringTicketModal(false)
          }}
          data-testid="delete-recurring-dispatch-ticket-modal"
          onSubmit={onDeleteRecurringDispatchTicket}
        />
      )}
    </FormProvider>
  )
}

export default DispatchTicketContainer
